import React, { useState } from 'react';
import styled from 'styled-components';
import { v4 as uuid4 } from 'uuid';
import * as size from '@constant/size';
import * as color from '@constant/color';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import DesignOption from '@design-system/option';
import TextButton from '@design-system/text-button';
import { ETheme } from '@datatype/type';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import { actions as studentActions } from '@store/slice/student-slice';
import { TIyagigoSubMenu } from '@datatype/iyagigo/type';
import { EApiLanguageCode, EApiVocabularyBookCategory, EApiVocabularyBookLevel, IModifyStudentRequest } from '@datatype/iyagigo/api-type';
import { useModifyStudentMutation } from '@hook/iyagigo/useApi';
import { useMySubscriptionQuery } from '@hook/billing/useApi';
import { LOCAL_STORAGE_STUDENT_KEY } from '@config/auth';
import {
  JAPANESE_TEST_VOCABULARY_BOOK_LEVEL_OPTIONS,
  ENGLISH_TEST_VOCABULARY_BOOK_LEVEL_OPTIONS,
  STUDY_VOCABULARY_BOOK_LEVEL_OPTIONS,
} from '@constant/variable';
import { EApiSubscriptionStatus } from '@datatype/billing/api-type';

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: ${size.MEDIUM_MODAL_WIDTH};
`;

const OptionContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 5px;
  width: 100%;
`;

const VocabularyBookStudyConfigurationModal: React.FC = () => {
  const dispatch = useTypedDispatch();
  // 시험단어집(TEST) 미노출 — 카테고리는 STUDY 고정. subMenu는 단어집/단어노트 탭 축이라 카테고리를 결정하지 않음.
  // TEST 재노출 시 subMenu 기반 카테고리 분기 부활.
  // const vocabularyBookSubMenu = useTypedSelector(
  //   (state) => state.rootReducer?.navigationReducer?.iyagigo?.subMenu,
  // ) as TIyagigoSubMenu['vocabulary-book'];
  // const vocabularyBookType =
  //   vocabularyBookSubMenu === 'vocabulary-book-study' ? EApiVocabularyBookCategory.STUDY : EApiVocabularyBookCategory.TEST;
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const apiLanguageCode =
    studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE;
  const modifyStudent = useModifyStudentMutation();
  const subscriptionQuery = useMySubscriptionQuery();
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const isSubscriber = subscriptionQuery.data?.data?.status === EApiSubscriptionStatus.ACTIVE;

  // Redux (studentReducer)에서 언어별 STUDY 레벨 매칭. TEST 재노출 시 카테고리 분기 부활.
  const currentEnglishStudyVocabularyBookLevel = useTypedSelector(
    (state) => state.rootReducer?.studentReducer?.currentEnglishStudyVocabularyBookLevel,
  );
  const currentJapaneseStudyVocabularyBookLevel = useTypedSelector(
    (state) => state.rootReducer?.studentReducer?.currentJapaneseStudyVocabularyBookLevel,
  );

  // 시험단어집(TEST) 미노출 — STUDY 옵션 고정. TEST 재노출 시 아래 분기 부활.
  // const OPTIONS = (() => {
  //   if (vocabularyBookType === EApiVocabularyBookCategory.STUDY) {
  //     return STUDY_VOCABULARY_BOOK_LEVEL_OPTIONS;
  //   }
  //   if (apiLanguageCode === EApiLanguageCode.ENGLISH) {
  //     return ENGLISH_TEST_VOCABULARY_BOOK_LEVEL_OPTIONS;
  //   }
  //   return JAPANESE_TEST_VOCABULARY_BOOK_LEVEL_OPTIONS;
  // })();
  const OPTIONS = STUDY_VOCABULARY_BOOK_LEVEL_OPTIONS;

  const configuredLevel =
    apiLanguageCode === EApiLanguageCode.ENGLISH
      ? currentEnglishStudyVocabularyBookLevel
      : currentJapaneseStudyVocabularyBookLevel;

  const [selectedLevel, setSelectedLevel] = useState<EApiVocabularyBookLevel | null>(configuredLevel ?? null);

  const handleSave = () => {
    if (!selectedLevel) return;
    if (!isSubscriber) {
      dispatch(
        modalActions.showWallToast({
          type: 'subscription-wall',
          data: { trigger: 'access' },
        }),
      );
      return;
    }

    const request: IModifyStudentRequest = { studentKey };
    // 시험단어집(TEST) 미노출 — STUDY 레벨만 저장. TEST 재노출 시 카테고리 분기 부활.
    if (apiLanguageCode === EApiLanguageCode.ENGLISH) {
      request.englishStudyVocabularyBookLevel = selectedLevel;
      dispatch(studentActions.storeCurrentEnglishStudyVocabularyBookLevel(selectedLevel));
    } else {
      request.japaneseStudyVocabularyBookLevel = selectedLevel;
      dispatch(studentActions.storeCurrentJapaneseStudyVocabularyBookLevel(selectedLevel));
    }
    modifyStudent.mutate(request);
    dispatch(modalActions.hideModal());
  };

  return (
    <Wrapper>
      <TextBlock
        text='학습할 단어집을 선택해주세요'
        design={{
          text: { size: 'medium', color: color.BLACK_2 },
          box: { alignX: 'center' },
        }}
      />
      <Gap height={size.MARGIN_1} />
      <OptionContainer>
        {OPTIONS.map((option) => (
          <DesignOption
            key={uuid4()}
            theme={ETheme.BROWN}
            width='100%'
            label={option.label}
            value={option.content}
            selected={option.value === selectedLevel}
            handleClick={() => setSelectedLevel(option.value as EApiVocabularyBookLevel)}
          />
        ))}
      </OptionContainer>
      <Gap height={size.MARGIN_2} />
      <TextButton
        text='저장하기'
        theme={ETheme.BROWN}
        size='medium'
        shape='pill'
        width='100%'
        disabled={selectedLevel === null}
        handleClick={handleSave}
      />
    </Wrapper>
  );
};

export default VocabularyBookStudyConfigurationModal;
